import { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import { Language } from '@weave/schema-gen-ts/dist/schemas/phone/audio-library/v1/audio_library_service.pb';
import { useDropzone } from 'react-dropzone';
import { useTranslation, Trans } from 'react-i18next';
import { AudioLibraryApi } from '@frontend/api-audio-library';
import { getJTI } from '@frontend/auth-helpers';
import { getInitialParams } from '@frontend/env';
import { i18next } from '@frontend/i18n';
import { Icon } from '@frontend/icons';
import { useQuery } from '@frontend/react-query-helpers';
import { theme } from '@frontend/theme';
import {
  ModalControlModalProps,
  Text,
  useFormField,
  useAlert,
  Modal,
  ContentLoader,
  Heading,
  TextField,
  SecondaryButton,
  PrimaryButton,
  Tabs,
  TextareaField,
  DropdownField,
  Button,
  AudioScrubber,
  useForm,
  TextLink,
  NakedButton,
  SpinningLoader,
} from '@frontend/design-system';
import { trackingId } from '../../tracking';
import { validateAudioFileName } from '../../utils/validators';
import { useAudioMutations } from './use-audio-mutations';
import { audioFiles } from './voices-data';

// TTS Voice configuration constants
export const ALLOWED_VOICES = ['en-US-Standard-F', 'en-US-Standard-J', 'es-US-News-F', 'es-US-Polyglot-1'] as const;

export const LANGUAGE_LABELS: Record<string, string> = {
  LANGUAGE_EN_US: 'English',
  LANGUAGE_ES_US: 'Spanish',
} as const;

export const LANGUAGE_API_MAP: Record<string, string> = {
  LANGUAGE_EN_US: 'en-US',
  LANGUAGE_ES_US: 'es-US',
} as const;

export const DEFAULT_VOICES: Record<string, string> = {
  LANGUAGE_EN_US: 'en-US-Standard-F',
  LANGUAGE_ES_US: 'es-US-News-F',
} as const;

export const FEMALE_VOICES = ['en-US-Standard-F', 'es-US-News-F'] as const;

//Translated language labels for TTS language dropdown.
export const TRANSLATED_LANGUAGE_LABELS: Record<string, string> = {
  LANGUAGE_EN_US: i18next.t('English', { ns: 'phone' }),
  LANGUAGE_ES_US: i18next.t('Spanish', { ns: 'phone' }),
} as const;

export const AudioUploadModal = ({
  modalProps,
  onSuccess,
  hideTTS = false,
}: {
  modalProps: ModalControlModalProps;
  onSuccess?: (uploadedId: string) => void;
  hideTTS?: boolean;
}) => {
  const { t } = useTranslation('phone');
  const [activeTab, setActiveTab] = useState('upload');

  useEffect(() => {
    if (!modalProps.show) {
      setActiveTab('upload');
    }
  }, [modalProps.show]);

  return (
    <Modal {...modalProps} minWidth={800}>
      <Modal.Header onClose={modalProps.onClose}>{t('Add Audio File')}</Modal.Header>
      <Modal.Body>
        {!hideTTS ? (
          <Tabs initialTab={activeTab} onChange={setActiveTab}>
            <Tabs.Bar css={{ marginBottom: theme.spacing(2) }}>
              <Tabs.Tab id='upload'>{t('Upload Audio')}</Tabs.Tab>
              <Tabs.Tab
                id='generate'
                trackingId={trackingId({
                  context: 'setting',
                  feature: 'audio-library',
                  details: 'tts-generate-audio-tab',
                })}
              >
                <div css={{ display: 'flex', gap: theme.spacing(0.5), alignItems: 'center' }}>
                  {t('Generate Audio from Text')}{' '}
                  <Icon css={{ color: theme.colors.primary20 }} name='aiassistant-small' />
                </div>
              </Tabs.Tab>
            </Tabs.Bar>
            <Tabs.Panel controller='upload'>
              <AudioUploadModalContent modalProps={modalProps} onSuccess={onSuccess} setActiveTab={setActiveTab} />
            </Tabs.Panel>
            <Tabs.Panel controller='generate'>
              <GenerateAudioModalComponent modalProps={modalProps} onSuccess={onSuccess} />
            </Tabs.Panel>
          </Tabs>
        ) : (
          <AudioUploadModalContent modalProps={modalProps} onSuccess={onSuccess} hideTTS={hideTTS} />
        )}
      </Modal.Body>
    </Modal>
  );
};

// Helper function that takes in a filename and returns the filename without its extension.
const removeFileExtension = (fileName?: string) => {
  if (!fileName) return '';
  const lastDotIndex = fileName.lastIndexOf('.');
  return lastDotIndex === -1 ? fileName : fileName.substring(0, lastDotIndex);
};

export const AudioUploadModalContent = ({
  modalProps,
  customUploadFn,
  isUploading,
  onSuccess,
  setActiveTab,
  hideTTS,
}: {
  modalProps: ModalControlModalProps;
  customUploadFn?: (payload: { file: File; fileName: string }) => void;
  isUploading?: boolean;
  onSuccess?: (uploadedId: string) => void;
  setActiveTab?: (tab: string) => void;
  hideTTS?: boolean;
}) => {
  const { t } = useTranslation('phone');
  const [mediaFile, setMediaFile] = useState<File>();
  const nameFieldProps = useFormField(
    {
      type: 'text',
      value: removeFileExtension(mediaFile?.name),
      validator: validateAudioFileName,
      validateOnChange: true,
    },
    [mediaFile?.name]
  );

  const { uploadMutation } = useAudioMutations({
    onSuccess,
    onClose: modalProps.onClose,
  });

  return (
    <>
      <ContentLoader message={t('Uploading')} show={isUploading || uploadMutation.isLoading} />
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: theme.spacing(4),
          padding: theme.spacing(1, 0),
        }}
      >
        {!mediaFile && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: theme.spacing(4) }}>
            {!hideTTS && (
              <section
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'auto 1fr',
                  gridTemplateAreas: '"icon heading" ". description"',
                  rowGap: theme.spacing(1),
                  columnGap: theme.spacing(2),
                  alignItems: 'center',
                }}
              >
                <Icon name='aiassistant' css={{ gridArea: 'icon' }} />
                <TextLink
                  weight='bold'
                  onClick={() => setActiveTab && setActiveTab('generate')}
                  css={{
                    cursor: 'pointer',
                    gridArea: 'heading',
                    display: 'inline-flex',
                    alignItems: 'center',
                    fontSize: theme.fontSize(20),
                  }}
                  trackingId={trackingId({
                    context: 'setting',
                    feature: 'audio-library',
                    details: 'tts-generate-audio-tab-link',
                  })}
                >
                  {t('Generate audio from text')}
                </TextLink>
                <Text css={{ gridArea: 'description' }}>{t('Type a message to generate an audio file.')}</Text>
              </section>
            )}
            <section
              style={{
                display: 'grid',
                gridTemplateColumns: 'auto 1fr',
                gridTemplateAreas: '"icon heading" ". description"',
                rowGap: theme.spacing(1),
                columnGap: theme.spacing(2),
                alignItems: 'center',
              }}
            >
              <Icon name='phone' css={{ gridArea: 'icon' }} />
              <Heading level={3} css={{ gridArea: 'heading' }}>
                {t('Record an audio file from your phone')}
              </Heading>
              <Text css={{ gridArea: 'description' }}>
                {t(
                  'To record a message using your Weave desk phone, dial *86 and record your message following the prompt. Refresh the page to see your new file appear.'
                )}
              </Text>
            </section>
            <section
              style={{
                display: 'grid',
                gridTemplateColumns: 'auto 1fr',
                gridTemplateAreas: '"icon heading" ". description"',
                rowGap: theme.spacing(1),
                columnGap: theme.spacing(2),
                alignItems: 'center',
              }}
            >
              <Icon name='cell-phone' css={{ gridArea: 'icon' }} />
              <Heading level={3} css={{ gridArea: 'heading' }}>
                {t('Record an audio file from the mobile app')}
              </Heading>
              <Text css={{ gridArea: 'description' }}>
                {t(
                  `To record a message using your mobile app, open the app and go into Audio Library tapping Settings > Phones > Audio Library then choose "Record New."`
                )}
              </Text>
            </section>
            <section
              style={{
                display: 'grid',
                gridTemplateColumns: 'auto 1fr',
                gridTemplateAreas: '"icon heading"',
                columnGap: theme.spacing(2),
                alignItems: 'center',
              }}
            >
              <Icon name='upload' css={{ gridArea: 'icon' }} />
              <Heading level={3} css={{ gridArea: 'heading' }}>
                {t('Upload an existing audio file')}
              </Heading>
            </section>
          </div>
        )}
        <MediaFileDropzone onChange={(file) => setMediaFile(file)} fileName={mediaFile?.name ?? ''} />
        {!!mediaFile && (
          <div style={{ width: '100%', display: 'flex', flexDirection: 'column', gap: theme.spacing(2) }}>
            <TextField
              {...nameFieldProps}
              name='phoneMediaName'
              label={t('File Name')}
              helperText={t('Give your file a name you can remember')}
            />

            <div style={{ display: 'flex', gap: theme.spacing(1), width: '100%', justifyContent: 'end' }}>
              <SecondaryButton
                onClick={() => {
                  setMediaFile(undefined);
                }}
                style={{ width: 'fit-content' }}
              >
                {t('Cancel')}
              </SecondaryButton>
              <PrimaryButton
                onClick={() => {
                  if (customUploadFn) {
                    customUploadFn({ file: mediaFile, fileName: nameFieldProps.value });
                  } else {
                    uploadMutation.mutate({ file: mediaFile, fileName: nameFieldProps.value });
                    setMediaFile(undefined);
                  }
                }}
                style={{ width: 'fit-content' }}
              >
                {t('Upload File')}
              </PrimaryButton>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

const MediaFileDropzone = ({ onChange, fileName }: { onChange: (file: File) => void; fileName: string }) => {
  const { t } = useTranslation('phone', { keyPrefix: 'media' });

  const [error, setError] = useState('');

  const onDrop = useCallback((acceptedFiles: any, rejectedFiles: any) => {
    if (acceptedFiles.length > 0) {
      const file: File = acceptedFiles[0];
      setError('');
      onChange(file);
    } else if (rejectedFiles.length > 0) {
      setError(t('Invalid file type: Must be WAV, MPEG (MP3) or MP4'));
    }
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    accept: { 'audio/*': ['.wav', '.mpeg', '.m4a'], 'video/*': ['.mp4'] },
    onDrop,
  });

  const onBrowseClick = useMemo(() => getRootProps().onClick, [getRootProps]);

  let content;
  if (isDragActive) {
    content = <Text>{t('Drop file here...')}</Text>;
  } else if (fileName) {
    content = (
      <Trans>
        <div>
          <Text as='span'>File: {''}</Text>
          <Text as='span' weight='bold'>
            {fileName}
          </Text>
        </div>
      </Trans>
    );
  } else {
    content = (
      <div
        css={{
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          gap: theme.spacing(1),
        }}
      >
        <Icon name='up' size={20} />
        <Text size='medium' as='span'>
          {t('Drop WAV, MPEG (MP3), or MP4 file here')}
        </Text>
        <SecondaryButton style={{ width: 'fit-content' }} onClick={onBrowseClick}>
          {t('Choose File')}
        </SecondaryButton>
      </div>
    );
  }

  return (
    <div
      css={{
        cursor: 'pointer',
        backgroundColor: theme.colors.neutral5,
        border: `1px dashed ${theme.colors.neutral30}`,
        borderRadius: theme.borderRadius.small,
        display: 'flex',
        WebkitBoxPack: 'center',
        justifyContent: 'center',
        WebkitBoxAlign: 'center',
        alignItems: 'center',
        width: '100%',
        padding: theme.spacing(2),
        alignSelf: 'center',
      }}
      {...getRootProps()}
    >
      <input {...getInputProps()} />
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100%',
        }}
      >
        {content}
        {error && (
          <div
            css={{
              color: theme.colors.status.critical,
              marginTop: theme.spacing(2),
            }}
          >
            {error}
          </div>
        )}
      </div>
    </div>
  );
};

export const GenerateAudioModalComponent = ({
  modalProps,
  onSuccess,
  customUploadFn,
}: {
  modalProps: ModalControlModalProps;
  onSuccess?: (uploadedId: string) => void;
  customUploadFn?: (payload: { file: File; fileName: string }) => void;
}) => {
  const { t } = useTranslation('phone', { keyPrefix: 'media' });
  const alerts = useAlert();
  const [audioSrc, setAudioSrc] = useState<string>('');
  const hasFocusedRef = useRef(false);

  // Automatically focus the "File Name" input when the user switches to the "Generate Audio" tab.
  // `autoFocus` doesn't work since the field doesn't exist in the DOM on initial render.
  useEffect(() => {
    const tryFocus = () => {
      if (hasFocusedRef.current) return;

      const input = document.querySelector('input[name="name"]') as HTMLInputElement | null;

      if (input && input.offsetParent !== null) {
        input.focus();
        hasFocusedRef.current = true;
      } else {
        requestAnimationFrame(tryFocus);
      }
    };

    requestAnimationFrame(tryFocus);
  }, []);

  const { uploadMutation, generateMutation } = useAudioMutations({
    onSuccess,
    onClose: modalProps.onClose,
    successMessage: t('Audio file successfully saved to Audio Library.'),
  });

  const { formProps, getFieldProps, isComplete, seedValues, reset } = useForm({
    fields: {
      name: { type: 'text', required: true, validator: validateAudioFileName, validateOnChange: true },
      message: { type: 'text', required: true },
      language: { type: 'dropdown', required: true, value: 'LANGUAGE_EN_US' },
      voice: { type: 'dropdown', required: true },
    },
  });

  const clickHandler = () => {
    generateMutation.mutate(
      {
        name: getFieldProps('name').value || 'tts-audio',
        message: getFieldProps('message').value || '',
        language: LANGUAGE_API_MAP[getFieldProps('language').value] || '',
        voice: getFieldProps('voice').value || '',
      },
      {
        onSuccess: (data) => {
          const audioUrl = URL.createObjectURL(data);
          setAudioSrc(audioUrl);
        },
        onError: () => {
          alerts.error(t('Failed to generate audio.'));
        },
      }
    );
  };

  const nameField = { ...getFieldProps('name') };
  const textField = { ...getFieldProps('message') };
  const languageField = { ...getFieldProps('language') };
  const voiceField = { ...getFieldProps('voice') };

  const { data: languages = [] } = useQuery({
    queryKey: ['tts', 'language-list'],
    queryFn: () => AudioLibraryApi.getAudioLibraryLanguages({}),
    select: (data) => data?.languages || [],
  });

  const { data: voices = [] } = useQuery({
    queryKey: ['tts', 'voices', languageField.value],
    queryFn: () =>
      AudioLibraryApi.getAudioLibraryVoices({
        option: {
          language: languageField.value as Language,
        },
      }),
    select: (data) => data?.voices || [],
  });

  const filteredVoices = voices.filter((voice: string) => ALLOWED_VOICES.includes(voice));

  const MAX_CHARS = 1000;
  const WARNING_THRESHOLD = 900;

  const message = getFieldProps('message').value || '';
  const messageLength = message.length;
  const charsRemaining = MAX_CHARS - messageLength;
  const isMessageOverLimit = messageLength > MAX_CHARS;

  // Set default voice when language changes
  useEffect(() => {
    const selectedLanguage = getFieldProps('language').value;
    const currentVoice = getFieldProps('voice').value;

    if (filteredVoices.length > 0 && !filteredVoices.includes(currentVoice)) {
      let defaultVoice = DEFAULT_VOICES[selectedLanguage];
      // Fallback if preferred default not in available options
      if (!defaultVoice || !filteredVoices.includes(defaultVoice)) {
        defaultVoice = filteredVoices[0];
      }

      seedValues({ voice: defaultVoice });
    }
  }, [filteredVoices, getFieldProps('language').value]);

  useEffect(() => {
    if (isComplete) {
      setAudioSrc('');
    }
  }, [getFieldProps('voice').value, getFieldProps('language').value, getFieldProps('message').value]);

  // Check if the required fields for generation (excluding 'name') are filled
  const isFormReadyForGenerate = (['message', 'language', 'voice'] as const).every((field) => {
    const value = getFieldProps(field).value;
    return value !== undefined && value !== '';
  });

  // Prevent generation if the current form values match the last generated input
  const hasGeneratedForCurrentInput = () => {
    const generated = generateMutation.variables;
    return (
      generated?.message === getFieldProps('message').value &&
      generated?.language === LANGUAGE_API_MAP[getFieldProps('language').value] &&
      generated?.voice === getFieldProps('voice').value
    );
  };

  const shouldEnableGenerate =
    isFormReadyForGenerate && (!generateMutation.isSuccess || !hasGeneratedForCurrentInput());

  const getFileFromBlobUrl = (blobUrl: string, fileName: string): Promise<File> => {
    return new Promise((resolve, reject) => {
      fetch(blobUrl)
        .then((response) => response.blob())
        .then((blob) => {
          const file = new File([blob], fileName, { type: blob.type });
          resolve(file);
        })
        .catch(reject);
    });
  };

  const uploadAudioGreeting = async () => {
    const name = nameField.value;
    const messageText = getFieldProps('message').value || '';
    const language = LANGUAGE_API_MAP[getFieldProps('language').value] || '';
    const voice = getFieldProps('voice').value || '';

    try {
      let finalAudioSrc = audioSrc;

      // Generate if not already available
      if (!finalAudioSrc) {
        const audioBlob = await generateMutation.mutateAsync({
          name,
          message: messageText,
          language,
          voice,
        });

        finalAudioSrc = URL.createObjectURL(audioBlob);
        setAudioSrc(finalAudioSrc);
      }

      // Now upload using finalAudioSrc (either from before or just generated)
      const file = await getFileFromBlobUrl(finalAudioSrc, `${name}.mp3`);
      if (customUploadFn) {
        customUploadFn({ file, fileName: name });
      } else {
        uploadMutation.mutate({ file, fileName: name });
      }
    } catch (err) {
      alerts.error(t('Failed to generate or upload audio.'));
      console.error('Audio upload flow error:', err);
    }
  };

  const closeHandler = () => {
    reset();
    modalProps.onClose();
  };

  const [isPlayingVoice, setIsPlayingVoice] = useState<string | null>(null);
  const audioRef = useRef<HTMLAudioElement | null>(null);

  const handlePlayVoice = async (voiceName: string) => {
    if (typeof window !== 'undefined' && (window as any).Cypress) return;
    const audioFile = audioFiles.find((f: { FileName: string }) => f.FileName === voiceName);
    if (!audioFile?.FilePath || !audioFile?.MediaID) return;

    // Always pause and clean up current audio if any
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current = null;
    }

    // If clicking on the same voice, stop and reset
    if (isPlayingVoice === voiceName) {
      setIsPlayingVoice(null);
      return;
    }

    const jtiToken = getJTI();
    const currentEnv = getInitialParams().backendApi;
    const filePath = `${currentEnv}/phone/audio-library/v1/system/download/${audioFile.MediaID}`;

    try {
      const url = new URL(filePath);
      const headers: Record<string, string> = {};

      if (!url.searchParams.has('token')) {
        headers['Authorization'] = `Bearer ${jtiToken}`;
      }

      const response = await fetch(filePath, { headers });

      if (!response.ok) {
        console.error('Failed to fetch audio:', response.statusText);
        return;
      }

      const blob = await response.blob();
      const audioUrl = URL.createObjectURL(blob);

      const audio = new Audio(audioUrl);
      audioRef.current = audio;

      audio.play();
      setIsPlayingVoice(voiceName);

      audio.onended = () => {
        setIsPlayingVoice(null);
        audioRef.current = null;
      };
    } catch (err) {
      console.error('Audio play error:', err);
      setIsPlayingVoice(null);
    }
  };

  return (
    <div>
      <form
        {...formProps}
        style={{ display: 'grid', gap: theme.spacing(2) }}
        onSubmit={(e) => {
          e.preventDefault();
          e.stopPropagation();
          uploadAudioGreeting();
          modalProps.onClose();
        }}
      >
        <TextField
          {...nameField}
          label={t('File Name')}
          helperText={t('Give your audio file a name you can remember.')}
        />
        <DropdownField {...languageField} name='language' label={t('Language')}>
          {languages.map((option: string) => (
            <DropdownField.Option key={option} value={option}>
              {TRANSLATED_LANGUAGE_LABELS[option] || option} {/* Fallback to option if label is not found */}
            </DropdownField.Option>
          ))}
        </DropdownField>
        <DropdownField {...voiceField} name='voice' label={t('Voice')}>
          {filteredVoices.map((voice: string) => {
            const formattedLabel = FEMALE_VOICES.includes(voice) ? t('Female') : t('Male');
            return (
              <DropdownField.Option key={voice} value={voice} displayValue={formattedLabel}>
                <div css={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <span>{formattedLabel}</span>
                  <div
                    onClick={(e) => {
                      e.stopPropagation();
                      handlePlayVoice(voice);
                    }}
                  >
                    <Icon color='primary' name={isPlayingVoice === voice ? 'stop-small' : 'play-small'} />
                  </div>
                </div>
              </DropdownField.Option>
            );
          })}
        </DropdownField>
        <div css={{ display: 'flex', flexDirection: 'column', gap: theme.spacing(1) }}>
          <TextareaField {...textField} label={t('Message')} max={MAX_CHARS} />
          {messageLength >= WARNING_THRESHOLD && (
            <Text color={charsRemaining < 0 ? 'error' : 'subdued'}>
              {charsRemaining < 0
                ? t('{{count}} characters over limit', { count: Math.abs(charsRemaining) })
                : t('{{count}} characters remaining', { count: charsRemaining })}
            </Text>
          )}
        </div>
        <Heading level={3}>{t('Preview your audio')}</Heading>
        <AudioPreviewGenerator
          buttonText={t('Generate Audio')}
          isLoading={generateMutation.isLoading}
          isDisabled={!shouldEnableGenerate}
          onGenerate={clickHandler}
          audioSrc={audioSrc}
          generateButtonTrackingId={trackingId({
            context: 'setting',
            feature: 'audio-library',
            details: 'tts-generate-audio-btn',
          })}
        />
        <div css={{ display: 'flex', justifyContent: 'flex-end', gap: theme.spacing(2) }}>
          <Button variant='secondary' onClick={closeHandler}>
            {t('Cancel')}
          </Button>
          <Button
            variant='primary'
            type='submit'
            disabled={!isComplete || isMessageOverLimit}
            trackingId={trackingId({ context: 'setting', feature: 'audio-library', details: 'tts-save-audio-btn' })}
          >
            {t('Save')}
          </Button>
        </div>
      </form>
    </div>
  );
};

const audioScrubberStyle = {
  width: 284,
  height: 76,
  background: theme.colors.neutral5,
  borderRadius: theme.borderRadius.medium,
  padding: theme.spacing(2),
  '.audio-scrubber-input-container': {
    width: theme.spacing(13),
  },
};

/**
 * Reusable component for generating and previewing audio.
 * Displays a generate button and an audio scrubber/placeholder.
 */
export const AudioPreviewGenerator = ({
  buttonText,
  isLoading,
  isDisabled,
  onGenerate,
  audioSrc,
  generateButtonTrackingId,
  audioScrubberTrackingId,
  autoplay = true,
}: {
  /** Text to display on the generate button */
  buttonText: string;
  /** Whether audio generation is in progress */
  isLoading: boolean;
  /** Whether the generate button should be disabled */
  isDisabled: boolean;
  /** Callback when generate button is clicked */
  onGenerate: () => void;
  /** URL of the generated audio to preview */
  audioSrc: string;
  /** Optional tracking ID for the generate button */
  generateButtonTrackingId?: string;
  audioScrubberTrackingId?: string;
  /** Whether to autoplay the audio when it loads (default: true) */
  autoplay?: boolean;
}) => {
  const { t } = useTranslation('phone', { keyPrefix: 'media' });

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: theme.spacing(1), width: '100%' }}>
      <Button
        variant='tertiary'
        disabled={isDisabled}
        onClick={onGenerate}
        trackingId={generateButtonTrackingId}
        css={{ width: 'fit-content' }}
      >
        <div
          css={{
            display: 'flex',
            gap: theme.spacing(0.5),
            alignItems: 'center',
            justifyContent: 'flex-start',
          }}
        >
          <Icon name='sync-small' /> {buttonText}
        </div>
      </Button>

      {isLoading ? (
        <div css={{ ...audioScrubberStyle, display: 'flex', justifyContent: 'center' }}>
          <SpinningLoader css={{ display: 'block' }} size='small' />
        </div>
      ) : audioSrc ? (
        <AudioScrubber
          alwaysShowProgressButton
          autoSize
          css={audioScrubberStyle}
          showCurrentTime
          singlePlayer
          src={audioSrc}
          autoplay={autoplay}
          trackingId={audioScrubberTrackingId}
        />
      ) : (
        <div data-audio-scrubber css={{ ...audioScrubberStyle, display: 'flex', alignItems: 'center' }}>
          <NakedButton
            css={{ cursor: 'unset', height: theme.spacing(5), width: theme.spacing(5), padding: theme.spacing(1) }}
          >
            <Icon name='play' color='disabled' />
          </NakedButton>
          <div
            css={{
              alignItems: 'center',
              display: 'inline-flex',
              height: theme.spacing(3),
              margin: theme.spacing(0, 2, 0, 0.5),
            }}
          >
            <div
              css={{
                background: theme.colors.neutral20,
                height: theme.spacing(0.625),
                width: theme.spacing(13),
                borderRadius: theme.borderRadius.small,
              }}
            />
          </div>
          <Text as='span' css={{ margin: 0, paddingRight: theme.spacing(1) }}>
            {t('-:-- / -:--')}
          </Text>
        </div>
      )}
    </div>
  );
};
